import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Payment, Franchise, Branch, Barber } from '../types';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Card } from '../components/ui/Card';
import { Gift, User, Scissors, CreditCard, Wallet, DollarSign, Building2, Smartphone, Clock, Filter, X, FileText, AlertTriangle, Download, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function AdminBillingPage() {
  const { user, customClaims } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedFranchise, setSelectedFranchise] = useState<string>('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [selectedBarber, setSelectedBarber] = useState<string>('');
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  // Load franchises, branches, barbers
  useEffect(() => {
    loadFranchises();
  }, []);

  useEffect(() => {
    if (selectedFranchise) {
      loadBranches(selectedFranchise);
      setSelectedBranch('');
      setSelectedBarber('');
    } else {
      setBranches([]);
      setBarbers([]);
    }
  }, [selectedFranchise]);

  useEffect(() => {
    if (selectedBranch) {
      loadBarbers(selectedBranch);
      setSelectedBarber('');
    } else {
      setBarbers([]);
    }
  }, [selectedBranch]);

  useEffect(() => {
    loadPayments();
  }, [user, customClaims]);

  const loadFranchises = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'franchises'));
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        franchiseId: doc.id,
      })) as Franchise[];
      setFranchises(data);
    } catch (error) {
      console.error('Error loading franchises:', error);
    }
  };

  const loadBranches = async (franchiseId: string) => {
    try {
      const q = query(
        collection(db, 'branches'),
        where('franchiseId', '==', franchiseId)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        branchId: doc.id,
      })) as Branch[];
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const loadBarbers = async (branchId: string) => {
    try {
      const q = query(
        collection(db, 'barbers'),
        where('branchId', '==', branchId),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        barberId: doc.id,
      })) as Barber[];
      setBarbers(data);
    } catch (error) {
      console.error('Error loading barbers:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);

      // Determine access level and filters
      const isSuperAdmin = customClaims?.role === 'super_admin';
      const userFranchiseId = user?.franchiseId || customClaims?.franchiseId;
      const userBranchId = user?.branchId || customClaims?.branchId;

      let q = query(collection(db, 'payments'), orderBy('completedAt', 'desc'));

      // Apply role-based filters
      if (!isSuperAdmin) {
        if (userBranchId) {
          q = query(
            collection(db, 'payments'),
            where('branchId', '==', userBranchId),
            orderBy('completedAt', 'desc')
          );
        } else if (userFranchiseId) {
          q = query(
            collection(db, 'payments'),
            where('franchiseId', '==', userFranchiseId),
            orderBy('completedAt', 'desc')
          );
        }
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        paymentId: doc.id,
      })) as Payment[];

      setPayments(data);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  // Generate available months from payments
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    payments.forEach(p => {
      if (p.completedAt) {
        const date = p.completedAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.add(monthKey);
      }
    });
    return Array.from(months).sort().reverse();
  }, [payments]);

  // Get month label for display
  const getMonthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  // Handle month selection - sets start/end dates automatically
  const handleMonthSelect = (monthKey: string) => {
    setSelectedMonth(monthKey);
    if (monthKey) {
      const [year, month] = monthKey.split('-');
      const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endOfMonth = new Date(parseInt(year), parseInt(month), 0);
      setStartDate(startOfMonth.toISOString().split('T')[0]);
      setEndDate(endOfMonth.toISOString().split('T')[0]);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  // Filter payments
  const filteredPayments = payments.filter(p => {
    // Franchise filter
    if (selectedFranchise && p.franchiseId !== selectedFranchise) return false;

    // Branch filter
    if (selectedBranch && p.branchId !== selectedBranch) return false;

    // Barber filter
    if (selectedBarber && p.barberId !== selectedBarber) return false;

    // Payment method filter
    if (selectedPaymentMethods.length > 0 && !selectedPaymentMethods.includes(p.paymentMethod)) {
      return false;
    }

    // Date range filter
    if (startDate || endDate) {
      const paymentDate = p.completedAt?.toDate();
      if (!paymentDate) return false;

      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && paymentDate < start) return false;
      if (end) {
        end.setHours(23, 59, 59, 999);
        if (paymentDate > end) return false;
      }
    }

    return true;
  });

  // Calculate totals
  const totalPayments = filteredPayments.length;
  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalPaid = filteredPayments.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalVoucherDiscount = filteredPayments
    .reduce((sum, p) => sum + (p.voucherDiscount || (p as any).discountAmount || 0), 0);

  // Calculate by payment method
  const paymentsByMethod = filteredPayments.reduce((acc, p) => {
    acc[p.paymentMethod] = (acc[p.paymentMethod] || 0) + p.paidAmount;
    return acc;
  }, {} as Record<string, number>);

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getPaymentMethodIcon = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'voucher':
        return <Gift className="w-4 h-4 text-accent" />;
      case 'card':
        return <CreditCard className="w-4 h-4 text-info" />;
      case 'cash':
        return <Wallet className="w-4 h-4 text-success" />;
      case 'transfer':
        return <Building2 className="w-4 h-4 text-accent" />;
      case 'bizum':
        return <Smartphone className="w-4 h-4 text-accent" />;
      default:
        return null;
    }
  };

  const getPaymentMethodText = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'voucher':
        return 'Vale';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      case 'transfer':
        return 'Transferencia';
      case 'bizum':
        return 'Bizum';
      default:
        return '-';
    }
  };

  const paymentMethodOptions = [
    { value: 'voucher', label: 'Vale', icon: Gift, color: 'amber' },
    { value: 'card', label: 'Tarjeta', icon: CreditCard, color: 'blue' },
    { value: 'cash', label: 'Efectivo', icon: Wallet, color: 'green' },
    { value: 'transfer', label: 'Transferencia', icon: Building2, color: 'purple' },
    { value: 'bizum', label: 'Bizum', icon: Smartphone, color: 'pink' },
  ];

  const togglePaymentMethod = (method: string) => {
    if (selectedPaymentMethods.includes(method)) {
      setSelectedPaymentMethods(selectedPaymentMethods.filter(m => m !== method));
    } else {
      setSelectedPaymentMethods([...selectedPaymentMethods, method]);
    }
  };

  const clearFilters = () => {
    setSelectedFranchise('');
    setSelectedBranch('');
    setSelectedBarber('');
    setSelectedPaymentMethods([]);
    setStartDate('');
    setEndDate('');
    setSelectedMonth('');
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredPayments.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = filteredPayments.map(p => ({
      'Fecha': formatDate(p.completedAt),
      'Cliente': p.clientName,
      'Barbero': p.barberName || 'Barbero',
      'Servicio': p.serviceName,
      'Duración (min)': p.serviceDuration || '-',
      'Precio (€)': (p.totalAmount / 100).toFixed(2),
      'Desc. Vale (€)': ((p.voucherDiscount || (p as any).discountAmount || 0) / 100).toFixed(2),
      'Cobrado (€)': (p.paidAmount / 100).toFixed(2),
      'Método': getPaymentMethodText(p.paymentMethod),
      'Notas': p.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pagos');

    // Auto-size columns
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(key.length, ...data.map(row => String(row[key as keyof typeof row]).length))
    }));
    ws['!cols'] = colWidths;

    const fileName = selectedMonth
      ? `facturacion_${selectedMonth}.xlsx`
      : `facturacion_${new Date().toISOString().split('T')[0]}.xlsx`;

    XLSX.writeFile(wb, fileName);
    toast.success('Excel descargado correctamente');
  };

  // Export to PDF
  const exportToPDF = () => {
    if (filteredPayments.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF('landscape');

    // Title
    const title = selectedMonth
      ? `Facturación - ${getMonthLabel(selectedMonth)}`
      : 'Facturación';
    doc.setFontSize(18);
    doc.text(title, 14, 22);

    // Summary
    doc.setFontSize(10);
    doc.text(`Total Pagos: ${totalPayments}`, 14, 32);
    doc.text(`Total Servicios: ${(totalAmount / 100).toFixed(2)}€`, 14, 38);
    doc.text(`Total Cobrado: ${(totalPaid / 100).toFixed(2)}€`, 14, 44);
    doc.text(`Desc. Vales: ${(totalVoucherDiscount / 100).toFixed(2)}€`, 14, 50);
    doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, 200, 32, { align: 'right' });

    // Table data
    const tableData = filteredPayments.map(p => [
      formatDate(p.completedAt),
      p.clientName,
      p.barberName || 'Barbero',
      p.serviceName,
      p.serviceDuration ? `${p.serviceDuration}min` : '-',
      `${(p.totalAmount / 100).toFixed(2)}€`,
      `${((p.voucherDiscount || (p as any).discountAmount || 0) / 100).toFixed(2)}€`,
      `${(p.paidAmount / 100).toFixed(2)}€`,
      getPaymentMethodText(p.paymentMethod),
    ]);

    autoTable(doc, {
      startY: 56,
      head: [[
        'Fecha',
        'Cliente',
        'Barbero',
        'Servicio',
        'Duración',
        'Precio',
        'Desc. Vale',
        'Cobrado',
        'Método',
      ]],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }, // Indigo
      alternateRowStyles: { fillColor: [245, 245, 255] },
    });

    const fileName = selectedMonth
      ? `facturacion_${selectedMonth}.pdf`
      : `facturacion_${new Date().toISOString().split('T')[0]}.pdf`;

    doc.save(fileName);
    toast.success('PDF descargado correctamente');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-2 rounded w-1/3"></div>
          <div className="h-64 bg-surface-2 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">
            Facturación y Pagos
          </h1>
          <p className="text-sm text-ink-muted mt-1">
            Gestión completa de pagos del sistema
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToExcel}
            disabled={filteredPayments.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-success hover:bg-success/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={exportToPDF}
            disabled={filteredPayments.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-danger hover:bg-danger/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-ink-muted" />
          <h3 className="text-lg font-semibold text-ink">Filtros</h3>
          {(selectedFranchise || selectedBranch || selectedBarber || selectedPaymentMethods.length > 0 || startDate || endDate || selectedMonth) && (
            <button
              onClick={clearFilters}
              className="ml-auto text-sm text-ink-muted hover:text-ink dark:hover:text-white flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Franchise Filter */}
          {customClaims?.role === 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Franquicia
              </label>
              <select
                value={selectedFranchise}
                onChange={(e) => setSelectedFranchise(e.target.value)}
                className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
              >
                <option value="">Todas las franquicias</option>
                {franchises.map(f => (
                  <option key={f.franchiseId} value={f.franchiseId}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Branch Filter */}
          {(customClaims?.role === 'super_admin' || customClaims?.role === 'franchise_owner') && (
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Sucursal
              </label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                disabled={customClaims?.role === 'super_admin' && !selectedFranchise}
                className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Todas las sucursales</option>
                {branches.map(b => (
                  <option key={b.branchId} value={b.branchId}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Barber Filter */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Barbero
            </label>
            <select
              value={selectedBarber}
              onChange={(e) => setSelectedBarber(e.target.value)}
              disabled={!selectedBranch}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Todos los barberos</option>
              {barbers.map(b => (
                <option key={b.barberId} value={b.barberId}>
                  {b.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Quick Month Filter */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Mes Rápido
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthSelect(e.target.value)}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
            >
              <option value="">Todos los meses</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {getMonthLabel(month)}
                </option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Desde
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setSelectedMonth(''); // Clear month selection when manually changing dates
              }}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Hasta
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setSelectedMonth(''); // Clear month selection when manually changing dates
              }}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
            />
          </div>
        </div>

        {/* Payment Methods Filter */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-ink-muted mb-2">
            Métodos de Pago
          </label>
          <div className="flex flex-wrap gap-2">
            {paymentMethodOptions.map(method => {
              const Icon = method.icon;
              const selected = selectedPaymentMethods.includes(method.value);
              return (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => togglePaymentMethod(method.value)}
                  className={`px-3 py-1.5 rounded-lg border-2 transition-all flex items-center gap-2 text-sm ${
                    selected
                      ? `border-${method.color}-500 bg-${method.color}-50 dark:bg-${method.color}-900/30 text-${method.color}-800 dark:text-${method.color}-300`
                      : 'border-hairline text-ink-muted hover:border-gray-400'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {method.label}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 border-info/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-info/100/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Pagos</p>
              <p className="text-2xl font-bold text-ink">{totalPayments}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-success/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-success/100/20 rounded-lg">
              <Wallet className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Servicios</p>
              <p className="text-2xl font-bold text-ink">
                {(totalAmount / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-info/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-info/100/20 rounded-lg">
              <CreditCard className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Cobrado</p>
              <p className="text-2xl font-bold text-ink">
                {(totalPaid / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-accent/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-accent/100/20 rounded-lg">
              <Gift className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Desc. Vales</p>
              <p className="text-2xl font-bold text-ink">
                {(totalVoucherDiscount / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Payment Methods Breakdown */}
      {Object.keys(paymentsByMethod).length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">
            Desglose por Método de Pago
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(paymentsByMethod).map(([method, amount]) => (
              <div key={method} className="flex items-center gap-3 p-3 bg-surface-2 rounded-lg">
                <div className="flex-shrink-0">
                  {getPaymentMethodIcon(method as Payment['paymentMethod'])}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-ink-muted truncate">
                    {getPaymentMethodText(method as Payment['paymentMethod'])}
                  </p>
                  <p className="text-sm font-bold text-ink">
                    {(amount / 100).toFixed(2)}€
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Table */}
      {filteredPayments.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="w-16 h-16 text-ink-subtle mx-auto mb-4" />
          <p className="text-ink-muted">
            No hay pagos registrados{(selectedFranchise || selectedBranch || selectedBarber || startDate || endDate) ? ' con los filtros seleccionados' : ''}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Barbero
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Servicio
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Duración
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Desc. Vale
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Cobrado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Método
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Notas
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-hairline">
                {filteredPayments.map((payment) => (
                  <tr key={payment.paymentId} className="hover:bg-surface-2 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-ink">
                      {formatDate(payment.completedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-ink-subtle" />
                        <span className="text-sm font-medium text-ink">
                          {payment.clientName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-ink-muted">
                      {payment.barberName || 'Barbero'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Scissors className="w-4 h-4 text-ink-subtle" />
                        <span className="text-sm text-ink">
                          {payment.serviceName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {payment.serviceDuration ? (
                        <div className="flex items-center justify-center gap-1 text-sm text-ink-muted">
                          <Clock className="w-3 h-3" />
                          {payment.serviceDuration}min
                        </div>
                      ) : (
                        <span className="text-sm text-ink-subtle">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <div className="flex flex-col items-end">
                        <span className="text-ink">
                          {(payment.totalAmount / 100).toFixed(2)}€
                        </span>
                        {(payment as any).priceWasAdjusted && (payment as any).originalServicePrice && (
                          <span className="text-xs text-ink-subtle line-through">
                            {((payment as any).originalServicePrice / 100).toFixed(2)}€
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-accent">
                      {(payment.voucherDiscount || (payment as any).discountAmount) ? (
                        <>-{((payment.voucherDiscount || (payment as any).discountAmount) / 100).toFixed(2)}€</>
                      ) : (
                        <span className="text-ink-subtle">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-success">
                      {(payment.paidAmount / 100).toFixed(2)}€
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="text-xs text-ink-muted">
                          {getPaymentMethodText(payment.paymentMethod)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {payment.notes ? (
                        <div className="flex items-start gap-2">
                          {/* Show warning icon if price was adjusted */}
                          {(payment as any).priceWasAdjusted ? (
                            <span title="Precio ajustado">
                              <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                            </span>
                          ) : (
                            <FileText className="w-4 h-4 text-ink-subtle flex-shrink-0 mt-0.5" />
                          )}
                          <span className="text-xs text-ink-muted line-clamp-2" title={payment.notes}>
                            {payment.notes}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-subtle">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
